import { useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import CreatableSelect from 'react-select/creatable';
import { getDurations, getTestDeadline } from '../../../../redux/visit';
import { optionsStyle } from '../composables';
import useDebounce from '../../../../utils/useDebounceHook';

const EMPTY_DEADLINE = {
	testDurationId: null,
	testDeadlineId: null,
	testDeadlineDurationName: null,
	testDeadlineName: null,
};

const TestDeadline = () => {
	const { values, setFieldValue } = useFormikContext();
	const { durations, testDeadline } = useSelector((state) => state.visits);
	const dispatch = useDispatch();

	const [byDuration, setByDuration] = useState(false);
	const [options, setOptions] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearch = useDebounce(searchTerm, 500);

	useEffect(() => {
		if (!values.testDeadline) return;

		const { testDurationId, testDeadlineId } = values.testDeadline;

		if (testDurationId) {
			setByDuration(true);
			const duration = durations.find(d => d.id === testDurationId);
			if (duration) {
				setOptions([{ value: duration.id, label: duration.name, isNew: false }]);
			}
		} else if (testDeadlineId) {
			setByDuration(false);
			const deadline = testDeadline.find(d => d.id === testDeadlineId);
			if (deadline) {
				setOptions([{ value: deadline.id, label: deadline.name, isNew: false }]);
			}
		}
	}, [values.testDeadline, durations, testDeadline]);

	useEffect(() => {
		if (!debouncedSearch) return;
		if (byDuration) {
			dispatch(getDurations({ search: debouncedSearch, per_page: 5, status: 'active' }));
		} else {
			dispatch(getTestDeadline({ search: debouncedSearch, per_page: 5 }));
		}
	}, [debouncedSearch, byDuration, dispatch]);

	useEffect(() => {
		const newOptions = byDuration
			? durations.map(d => ({ value: d.id, label: d.name, isNew: false }))
			: testDeadline.map(d => ({ value: d.id, label: d.name, isNew: false }));

		if (newOptions.length) {
			setOptions(prev => mergeUnique(prev, newOptions));
		}
	}, [durations, testDeadline, byDuration]);

	const mergeUnique = (prev, newOpts) =>
		[...prev, ...newOpts].filter(
			(opt, idx, self) => idx === self.findIndex((o) => o.value === opt.value)
		);

	const getSelected = () => {
		const val = values.testDeadline;
		if (!val) return null;

		if (byDuration) {
			if (val.testDurationId) {
				return options.find((opt) => opt.value === val.testDurationId) || null;
			}
			if (val.testDeadlineDurationName) {
				return { value: `new-${val.testDeadlineDurationName}`, label: val.testDeadlineDurationName, isNew: true };
			}
		} else {
			if (val.testDeadlineId) {
				return options.find((opt) => opt.value === val.testDeadlineId) || null;
			}
			if (val.testDeadlineName) {
				return { value: `new-${val.testDeadlineName}`, label: val.testDeadlineName, isNew: true };
			}
		}
		return null;
	};

	const handleChange = (val) => {
		if (!val) {
			setFieldValue('testDeadline', EMPTY_DEADLINE);
			return;
		}

		if (val.__isNew__ || val.isNew) {
			const newOption = { value: `new-${val.label}`, label: val.label, isNew: true };
			if (byDuration) {
				setFieldValue('testDeadline', {
					testDurationId: null,
					testDeadlineDurationName: val.label,
				});
			} else {
				setFieldValue('testDeadline', {
					testDeadlineId: null,
					testDeadlineName: val.label,
				});
			}
			setOptions((prev) => mergeUnique(prev, [newOption]));
		} else {
			if (byDuration) {
				setFieldValue('testDeadline', {
					testDurationId: val.value,
					testDeadlineDurationName: null,
				});
			} else {
				setFieldValue('testDeadline', {
					testDeadlineId: val.value,
					testDeadlineName: null,
				});
			}
		}
	};

	// ✅ checkbox toggle
	const handleByDurationOption = (event) => {
		setByDuration(event.target.checked);
		setOptions([]);
		setFieldValue('testDeadline', EMPTY_DEADLINE);
	};

	return (
		<Row className="my-3">
			<Card.Title>{t('VISIT.TEST_DEADLINE')}</Card.Title>
			<Col md={5}>
				<Card>
					<Card.Header className="d-flex justify-content-between align-items-center">
						<Form.Label>{t('VISIT.DEADLINE')}</Form.Label>
						<Form.Check
							type="checkbox"
							label={t('VISIT.BY_DURATION')}
							className="ml-auto"
							onChange={handleByDurationOption}
							checked={byDuration}
						/>
					</Card.Header>
					<Card.Body className="p-0 m-0">
						<CreatableSelect
							isMulti={false}
							menuPortalTarget={document.body}
							styles={optionsStyle}
							isClearable
							options={options}
							value={getSelected()}
							onChange={handleChange}
							onInputChange={(inputValue, { action }) => {
								if (action === 'input-change') {
									setSearchTerm(inputValue);
								}
							}}
							isValidNewOption={(inputValue, _selectValue, selectOptions) =>
								!!inputValue.trim() &&
								!selectOptions.some(
									(opt) => opt.label.toLowerCase() === inputValue.trim().toLowerCase()
								)
							}
							filterOption={(option, rawInput) =>
								(!getSelected() || getSelected().label.toLowerCase() !== option.label.toLowerCase()) &&
								option.label.toLowerCase().includes(rawInput.toLowerCase())
							}
						/>
					</Card.Body>
				</Card>
			</Col>
		</Row>
	);
};

export default TestDeadline;
